import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { useReportCharts, type ReportChart } from '../../../api/charts';
import type { DocumentLanguage } from '../../../api/documents';
import { useInspection } from '../../../api/inspections';
import { Breadcrumbs, ButtonLink, InsetText } from '../../../components/idsk';
import { LANGUAGE_AUTONYMS } from '../../../i18n';
import { useAnnouncer } from '../../../layout/Announcer';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { QueryError } from '../../common/QueryError';
import { LoadingPage } from '../../common/LoadingPage';
import { DocumentLanguageSelect, defaultDocumentLanguage } from '../documents/DocumentDownloads';
import { ReportChartView } from './ReportChartView';

/** Charts grouped by status form, in the order of the report. */
function byForm(charts: ReportChart[]): { form: ReportChart['form']; formType: string; charts: ReportChart[] }[] {
  const groups: { form: ReportChart['form']; formType: string; charts: ReportChart[] }[] = [];
  for (const c of charts) {
    const last = groups[groups.length - 1];
    if (last && last.formType === c.formType) last.charts.push(c);
    else groups.push({ form: c.form, formType: c.formType, charts: [c] });
  }
  return groups;
}

/**
 * "Štatistika": tilted 3D donut charts of the numeric values of a finished inspection – every field the
 * template marks "Zobraziť v grafe" – rendered from the frozen report snapshot, each with its data table. The
 * chart language is kept in the URL (`?jazyk=en`) like on the report page.
 */
export function ChartsPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { announce } = useAnnouncer();
  const [params, setParams] = useSearchParams();
  const requested = params.get('jazyk');
  const lang: DocumentLanguage = requested === 'en' || requested === 'sk' ? requested : defaultDocumentLanguage();
  const inspection = useInspection(id);
  const finished = inspection.data?.reportAvailable === true;
  const charts = useReportCharts(finished ? id : undefined, lang);
  usePageTitle([t('charts.title'), inspection.data?.number ?? t('common.loading'), t('inspections.title')]);

  const shownLang = useRef<string | null>(null);
  const data = charts.data;
  useEffect(() => {
    if (!data || charts.isPlaceholderData) return;
    if (shownLang.current && shownLang.current !== data.lang) {
      announce(t('charts.languageShown', { language: LANGUAGE_AUTONYMS[data.lang === 'en' ? 'en' : 'sk'] }));
    }
    shownLang.current = data.lang;
  }, [data, charts.isPlaceholderData, announce, t]);

  if (inspection.error) return <QueryError error={inspection.error} asPage />;
  if (inspection.isPending) return <LoadingPage />;
  const i = inspection.data;
  const crumbs = [
    { text: t('nav.home'), to: '/' },
    { text: t('inspections.title'), to: '/inspekcie' },
    { text: i.number, to: `/inspekcie/${i.id}` },
    { text: t('charts.title') },
  ];
  const heading = (
    <>
      <Breadcrumbs items={crumbs} />
      <PageHeading caption={i.number}>{t('charts.title')}</PageHeading>
    </>
  );

  if (!finished) {
    return (
      <>
        {heading}
        <p className="govuk-body">{t('charts.notYet')}</p>
        <ButtonLink to={`/inspekcie/${i.id}`} variant="secondary">
          {t('report.view.back')}
        </ButtonLink>
      </>
    );
  }
  if (charts.error) return <QueryError error={charts.error} asPage />;
  if (!data) return <LoadingPage />;

  return (
    <div className="app-charts-page">
      {heading}
      <p className="govuk-body">{t('charts.intro')}</p>
      <div className="app-report-toolbar app-no-print">
        <DocumentLanguageSelect id="charts-lang" value={lang} hint={t('charts.languageHint')} onChange={(l) => setParams({ jazyk: l }, { replace: true })} />
        <p className="govuk-body">
          <ButtonLink to={`/inspekcie/${i.id}/sprava?jazyk=${lang}`} variant="secondary">
            {t('report.view.open')}
          </ButtonLink>
        </p>
      </div>
      {charts.isPlaceholderData && (
        <InsetText>
          <span role="status">{t('common.loading')}</span>
        </InsetText>
      )}
      {data.charts.length === 0 ? (
        <InsetText>{t('charts.none')}</InsetText>
      ) : (
        <div lang={data.lang}>
          {byForm(data.charts).map((group) => (
            <section key={group.formType} aria-labelledby={`charts-${group.formType}`}>
              <h2 className="govuk-heading-m" id={`charts-${group.formType}`} lang={group.form.lang ?? undefined}>
                {group.form.value}
              </h2>
              {group.charts.map((chart) => (
                <ReportChartView key={chart.id} chart={chart} lang={data.lang} />
              ))}
            </section>
          ))}
        </div>
      )}
      <p className="govuk-body app-no-print">
        <ButtonLink to={`/inspekcie/${i.id}`} variant="secondary">
          {t('report.view.back')}
        </ButtonLink>
      </p>
    </div>
  );
}
